/**
 * Omsetning fra regnskapet (#183 phase 2): for a company on PowerOffice Go, the month's revenue per day
 * and its labour cost as the accounts have them (the store's department), beside the figures typed in
 * Timefly, and the year's budget as a wage target. The administrator picks the days to take; nothing
 * is written before «Hent». The server reads Go again and writes through the usual sales route.
 */
import { errorText, get, put } from '@/api/client';
import { T } from '@/i18n';
import { useTenant } from '@/shell/tenant';
import { Button, toast } from '@/ui';
import { fmtD } from '@/ui/format';
import { Notice } from '@/ui/page';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { POWEROFFICE, type PayrollSystem } from '../lonn/payrollSystem';
import { PowerOfficeWagePlan } from './PowerOfficeWagePlan';

interface Figures {
  department: { code: string; name: string } | null;
  /** each day's revenue in the accounts under the system's key (`poweroffice`, `tripletex`) */
  days: ({ day: string; timefly: number | null } & Partial<Record<PayrollSystem['key'], number | null>>)[];
  revenue: number;
  labour_cost: number;
  /** the company's payroll posted on no department, when the store reads its own department */
  labour_cost_unassigned?: number;
  /** some payroll is on no department, its amount not shown to this person (other stores' too) */
  labour_unassigned?: boolean;
}
interface Budget {
  year: number;
  revenue: number;
  labour_cost: number;
  wage_pct: number | null;
}
const kr = (v: number) => `${Math.round(v).toLocaleString('nb-NO')} kr`;

export function PowerOfficeRevenue({
  ym,
  target,
  sys = POWEROFFICE,
}: { ym: string; target: number; sys?: PayrollSystem }) {
  const qc = useQueryClient();
  const { tenant } = useTenant();
  const [y, m] = ym.split('-').map(Number) as [number, number];
  const from = `${ym}-01`;
  const to = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: [sys.key, 'accounts', ym],
    queryFn: () => get<Figures>(`${sys.base}/accounts?start_date=${from}&end_date=${to}`),
    enabled: open,
  });
  const b = useQuery({
    queryKey: [sys.key, 'budget', y],
    queryFn: () => get<Budget>(`${sys.base}/budget?year=${y}`),
    enabled: open,
  });
  const [pick, setPick] = useState<Set<string>>(new Set());
  const take = useMutation({
    mutationFn: () =>
      put<Figures>(`${sys.base}/accounts`, { start_date: from, end_date: to, days: [...pick] }),
    onSuccess: (r) => {
      qc.setQueryData([sys.key, 'accounts', ym], r);
      qc.invalidateQueries({ queryKey: ['sales'] });
      setPick(new Set());
      toast(T('Omsetningen er hentet fra {system}.', { system: sys.short }));
    },
    onError: (e) => toast(errorText(e)),
  });
  const useTarget = useMutation({
    mutationFn: (pct: number) => put('/admin/labour-settings', { wage_target_pct: pct }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['labour-settings'] });
      toast(T('Målet for lønnsprosent er satt fra budsjettet.'));
    },
    onError: (e) => toast(errorText(e)),
  });
  if (!open)
    return (
      <span>
        <Button small onClick={() => setOpen(true)}>
          {T('Hent omsetning fra {system}', { system: sys.short })}
        </Button>
      </span>
    );
  const f = q.data;
  const inAccounts = (d: Figures['days'][number]) => d[sys.key] ?? null;
  const differ = (f?.days ?? []).filter((d) => inAccounts(d) != null && inAccounts(d) !== d.timefly);
  return (
    <section data-testid="po-revenue" style={{ display: 'grid', gap: 10 }}>
      {q.isPending ? (
        <output style={{ color: 'var(--ink3)' }}>
          {T('Henter fra regnskapet i {system} …', { system: sys.short })}
        </output>
      ) : q.isError || !f ? (
        <Notice tone="dgr" title={T('Kunne ikke hente fra {system}', { system: sys.short })}>
          {errorText(q.error)}
        </Notice>
      ) : (
        <>
          <p style={{ margin: 0 }} data-testid="po-revenue-sum">
            {T('Regnskapet{dept}: omsetning {rev}, lønnskostnad {lab}', {
              dept: f.department ? ` (${f.department.name})` : '',
              rev: kr(f.revenue),
              lab: kr(f.labour_cost),
            })}
            {f.revenue > 0 && f.labour_cost > 0
              ? ` (${((f.labour_cost / f.revenue) * 100).toFixed(1).replace('.', ',')} %)`
              : ''}
          </p>
          {f.labour_unassigned && (
            <span style={{ fontSize: 13, color: 'var(--ink3)' }} data-testid="po-revenue-unassigned">
              {T(
                'Noe lønn er ført uten avdeling i regnskapet, for hele firmaet. Den er ikke regnet med i butikkens lønnskostnad.',
              )}
            </span>
          )}
          {(f.labour_cost_unassigned ?? 0) > 0 && (
            <span style={{ fontSize: 13, color: 'var(--ink3)' }} data-testid="po-revenue-unassigned">
              {T(
                'I tillegg er {lab} lønn ført uten avdeling i regnskapet, for hele firmaet. Den er ikke regnet med i butikkens lønnskostnad.',
                { lab: kr(f.labour_cost_unassigned ?? 0) },
              )}
            </span>
          )}
          {differ.length === 0 ? (
            <span style={{ color: 'var(--ink3)', fontSize: 13 }}>
              {T('Omsetningen i Timefly er lik regnskapet.')}
            </span>
          ) : (
            <table className="t">
              <thead>
                <tr>
                  <th />
                  <th>{T('Dag')}</th>
                  <th>{T('I Timefly')}</th>
                  <th>{T('I regnskapet')}</th>
                </tr>
              </thead>
              <tbody>
                {differ.map((d) => (
                  <tr key={d.day}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={T('Hent omsetningen for {day}', { day: fmtD(d.day) })}
                        checked={pick.has(d.day)}
                        onChange={(e) => {
                          const next = new Set(pick);
                          if (e.target.checked) next.add(d.day);
                          else next.delete(d.day);
                          setPick(next);
                        }}
                      />
                    </td>
                    <td>{fmtD(d.day)}</td>
                    <td>{d.timefly == null ? '–' : kr(d.timefly)}</td>
                    <td>{kr(inAccounts(d) ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {differ.length > 0 && (
            <span>
              <Button onClick={() => take.mutate()} disabled={take.isPending || pick.size === 0}>
                {take.isPending ? T('Henter…') : T('Hent {n} dager', { n: pick.size })}
              </Button>
            </span>
          )}
          {b.data?.wage_pct != null && (
            <span data-testid="po-budget" style={{ fontSize: 14 }}>
              {T('Budsjettet for {year}: lønnsprosent {pct} %.', {
                year: b.data.year,
                pct: String(b.data.wage_pct).replace('.', ','),
              })}{' '}
              {b.data.wage_pct !== target && (
                <button
                  type="button"
                  className="lnk"
                  onClick={() => useTarget.mutate(b.data?.wage_pct as number)}
                  disabled={useTarget.isPending}
                >
                  {T('Bruk som mål')}
                </button>
              )}
            </span>
          )}
          {/* the schedule's wage cost as Timefly's own budget in Go, the administrator's (#183 phase 3) */}
          {tenant?.me?.is_admin && sys.key === 'poweroffice' && <PowerOfficeWagePlan year={y} />}
        </>
      )}
    </section>
  );
}
